import { compareFileNames, isArchiveName, type ComicKind } from '@tanko/shared'
import { countArchivePages } from '../sources/archive-source'

const imageTypes: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
  gif: 'image/gif', avif: 'image/avif', bmp: 'image/bmp',
}

export function imageMediaType(name: string): string | undefined {
  return imageTypes[name.split('.').pop()?.toLowerCase() ?? '']
}

export async function imageFileNames(directory: FileSystemDirectoryHandle): Promise<string[]> {
  const names: string[] = []
  for await (const [name, handle] of directory.entries()) {
    if (handle.kind === 'file' && imageMediaType(name)) names.push(name)
  }
  return names.sort(compareFileNames)
}

export interface Candidate {
  /** 相對於來源的完整路徑，也是作品的識別依據。 */
  relPath: string
  /** 末端名稱，只用於顯示。 */
  name: string
  kind: ComicKind
  pageCount: number
  added: boolean
  error?: string
}

export interface CandidateScan {
  candidates: Candidate[]
  /** 有分支因為超過深度上限而沒有展開。 */
  truncated: boolean
}

/** 相對於來源根目錄的最大掃描深度。足以涵蓋「作品／單行本／第 N 卷」。 */
export const CANDIDATE_MAX_DEPTH = 4

/**
 * 從來源根目錄往下找出所有候選：直接含有圖片檔的資料夾，以及支援的壓縮檔。
 *
 * 一個資料夾只要自己含圖就是候選，不再往它底下遞迴——圖片已經在這一層，
 * 再往下多半是預覽圖之類的雜項。根目錄本身不列為候選（它是來源不是作品），
 * 但仍會往下走。
 */
export async function listCandidates(
  root: FileSystemDirectoryHandle,
  addedPaths: Set<string>,
): Promise<CandidateScan> {
  const candidates: Candidate[] = []
  let truncated = false

  async function scan(directory: FileSystemDirectoryHandle, prefix: string, depth: number): Promise<void> {
    const images: string[] = []
    const subdirectories: [string, FileSystemDirectoryHandle][] = []
    const archives: [string, FileSystemFileHandle][] = []
    for await (const [name, handle] of directory.entries()) {
      if (handle.kind === 'directory') subdirectories.push([name, handle])
      else if (imageMediaType(name)) images.push(name)
      else if (isArchiveName(name)) archives.push([name, handle])
    }

    for (const [name, handle] of archives) {
      const relPath = prefix ? `${prefix}/${name}` : name
      const base = { relPath, name, kind: 'archive' as const, added: addedPaths.has(relPath) }
      try {
        candidates.push({ ...base, pageCount: await countArchivePages(await handle.getFile()) })
      } catch (cause) {
        candidates.push({ ...base, pageCount: 0, error: errorMessage(cause) })
      }
    }

    if (images.length && depth > 0) {
      const name = prefix.slice(prefix.lastIndexOf('/') + 1)
      candidates.push({ relPath: prefix, name, kind: 'folder', pageCount: images.length, added: addedPaths.has(prefix) })
      return
    }

    for (const [name, handle] of subdirectories) {
      if (depth + 1 > CANDIDATE_MAX_DEPTH) { truncated = true; continue }
      const relPath = prefix ? `${prefix}/${name}` : name
      try {
        await scan(handle, relPath, depth + 1)
      } catch {
        candidates.push({ relPath, name, kind: 'folder', pageCount: 0, added: addedPaths.has(relPath), error: '無法讀取資料夾' })
      }
    }
  }

  await scan(root, '', 0)
  return { candidates: candidates.sort((a, b) => compareFileNames(a.relPath, b.relPath)), truncated }
}

export function errorMessage(error: unknown): string {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') return '瀏覽器未允許存取資料夾。請在電腦版 Chrome 或 Edge 開啟此頁，再按按鈕授權。'
    if (error.name === 'NotFoundError' || error.name === 'TypeMismatchError') return '找不到原本的資料夾或檔案。可以在管理來源重新選擇資料夾，或從作品選單更新它的位置。'
    if (error.name === 'QuotaExceededError') return '瀏覽器儲存空間不足，請先釋出空間再重試。'
  }
  return error instanceof Error ? error.message : '操作未完成，請再試一次。'
}
